/**
 * Phase 4.4.2 + 4.4.4: Patient Code Redemption UI + My Clinicians View
 *
 * Allows patients to:
 * - Enter invite codes to connect with private clinicians
 * - View their connected clinicians
 * - Navigate to book appointments with connected clinicians
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TicketIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { patientAccessAPI, ConnectedClinician, MyClinicianListResponse, RedeemCodeResponse } from '../../services/api';
import { formatShortDate } from '../../utils/timezone';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

// Design system colors
const colors = {
  primary: '#687666',
  primaryLight: '#b3bab2',
  cream: '#FFF4D7',
  dark: '#383934',
  accent: '#895A4C',
  gold: '#C2A67A',
};

interface RedeemCodeFormProps {
  onSuccess: (response: RedeemCodeResponse) => void;
}

const RedeemCodeForm: React.FC<RedeemCodeFormProps> = ({ onSuccess }) => {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format code as user types (add dash after 4 chars)
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length > 8) value = value.slice(0, 8);
    if (value.length > 4) {
      value = `${value.slice(0, 4)}-${value.slice(4)}`;
    }
    setCode(value);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const cleanCode = code.replace(/-/g, '');
    if (cleanCode.length !== 8) {
      setError('Please enter a valid 8-character code');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await patientAccessAPI.redeemCode(cleanCode);
      toast.success(`Connected to ${response.clinician_name}!`);
      setCode('');
      onSuccess(response);
    } catch (err: any) {
      const detail = apiErrorMessage(err, 'Failed to redeem code');
      setError(detail);
      toast.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-clinic-primary-lighter p-6">
      <div className="flex items-center mb-4">
        <TicketIcon className="h-6 w-6 mr-2" style={{ color: colors.gold }} />
        <h2 className="text-lg font-semibold text-clinic-dark">Redeem Invite Code</h2>
      </div>

      <p className="text-sm text-clinic-muted mb-4">
        Enter an invite code from your clinician to connect and book appointments.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
          <div className="flex-1">
            <input
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="XXXX-XXXX"
              className={`w-full px-4 py-3 text-base sm:text-lg font-mono text-center tracking-widest border rounded-md focus:outline-none focus:ring-2 ${
                error ? 'border-clinic-accent/30 focus:ring-clinic-accent/30' : 'border-clinic-primary-light focus:ring-primary-200'
              }`}
              style={{
                '--tw-ring-color': error ? undefined : `${colors.primary}40`,
              } as React.CSSProperties}
              maxLength={9}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting || code.replace(/-/g, '').length !== 8}
            className="w-full sm:w-auto px-6 py-3 min-h-[44px] border border-transparent text-sm font-medium rounded-md text-white disabled:opacity-50 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: colors.primary }}
          >
            {isSubmitting ? 'Connecting...' : 'Connect'}
          </button>
        </div>

        {error && (
          <div className="mt-2 flex items-center text-sm text-clinic-accent">
            <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
            {error}
          </div>
        )}
      </form>
    </div>
  );
};

interface ClinicianCardProps {
  clinician: ConnectedClinician;
  onDisconnect: (clinicianId: number) => void;
  isDisconnecting: boolean;
}

const ClinicianCard: React.FC<ClinicianCardProps> = ({
  clinician,
  onDisconnect,
  isDisconnecting,
}) => {
  const navigate = useNavigate();

  const handleBook = () => {
    if (clinician.booking_slug) {
      // Phase 4: Navigate via URL path parameter (refresh-safe, bookmarkable)
      // Enterprise pattern: /patient/book/:slug (Zocdoc, Epic MyChart, Doctolib)
      navigate(`/patient/book/${clinician.booking_slug}`);
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'invite_code':
        return 'Via Invite Code';
      case 'public_booking':
        return 'Public Booking';
      case 'legacy_assignment':
        return 'Assigned';
      default:
        return source;
    }
  };

  const formatDate = (dateStr: string) => {
    // Use clinic timezone for connected_at dates
    return formatShortDate(dateStr);
  };

  return (
    <div className="bg-white rounded-lg border border-clinic-primary-lighter p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {/* Avatar placeholder */}
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-medium text-lg"
            style={{ backgroundColor: colors.primary }}
          >
            {clinician.clinician_name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .slice(0, 2)}
          </div>

          <div>
            <h3 className="font-medium text-clinic-dark">{clinician.clinician_name}</h3>
            {clinician.clinician_specialty && (
              <p className="text-sm text-clinic-muted">{clinician.clinician_specialty}</p>
            )}
            <div className="flex items-center mt-1 text-xs text-clinic-muted">
              <span
                className="px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
              >
                {getSourceLabel(clinician.connection_source)}
              </span>
              <span className="mx-2">|</span>
              <span>Connected {formatDate(clinician.connected_at)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {clinician.can_book && clinician.booking_slug && (
            <button
              onClick={handleBook}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: colors.primary }}
            >
              <CalendarDaysIcon className="h-4 w-4 mr-1" />
              Book
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface RedeemSuccessModalProps {
  isOpen: boolean;
  response: RedeemCodeResponse | null;
  onClose: () => void;
  onViewClinician: () => void;
}

const RedeemSuccessModal: React.FC<RedeemSuccessModalProps> = ({
  isOpen,
  response,
  onClose,
  onViewClinician,
}) => {
  if (!isOpen || !response) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20">
        <div
          className="fixed inset-0 bg-clinic-muted bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative transform overflow-y-auto max-h-[90vh] rounded-lg bg-white shadow-xl transition-all w-full sm:max-w-md">
          <div className="bg-white px-6 py-8 text-center">
            <div
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-full mb-4"
              style={{ backgroundColor: `${colors.primary}20` }}
            >
              <CheckCircleIcon className="h-10 w-10" style={{ color: colors.primary }} />
            </div>

            <h3 className="text-xl font-semibold text-clinic-dark mb-2">
              Successfully Connected!
            </h3>

            <p className="text-clinic-muted mb-4">
              You are now connected to{' '}
              <span className="font-medium">{response.clinician_name}</span>
              {response.clinician_specialty && (
                <span className="text-clinic-muted">
                  {' '}
                  ({response.clinician_specialty})
                </span>
              )}
            </p>

            <p className="text-sm text-clinic-muted mb-6">
              You can now view their availability and book appointments.
            </p>

            <div className="flex flex-col space-y-3">
              {response.booking_slug && (
                <button
                  onClick={onViewClinician}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white hover:opacity-90"
                  style={{ backgroundColor: colors.primary }}
                >
                  View Clinician & Book
                  <ArrowRightIcon className="h-4 w-4 ml-2" />
                </button>
              )}
              <button
                onClick={onClose}
                className="w-full px-4 py-2 border border-clinic-primary-light text-sm font-medium rounded-md text-clinic-dark bg-white hover:bg-clinic-surface"
              >
                Stay Here
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MyClinicians: React.FC = () => {
  const navigate = useNavigate();
  const [clinicians, setClinicians] = useState<ConnectedClinician[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<number | null>(null);
  const [redeemSuccess, setRedeemSuccess] = useState<RedeemCodeResponse | null>(null);

  const fetchClinicians = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data: MyClinicianListResponse = await patientAccessAPI.getMyClinicians();
      setClinicians(data.items);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to load clinicians'));
      console.error('Error fetching clinicians:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClinicians();
  }, [fetchClinicians]);

  const handleRedeemSuccess = (response: RedeemCodeResponse) => {
    setRedeemSuccess(response);
    fetchClinicians();
  };

  const handleDisconnect = async (clinicianId: number) => {
    if (
      !window.confirm(
        'Are you sure you want to disconnect from this clinician? You will need a new invite code to reconnect.'
      )
    ) {
      return;
    }

    try {
      setDisconnectingId(clinicianId);
      await patientAccessAPI.disconnectFromClinician(clinicianId);
      setClinicians((prev) => prev.filter((c) => c.clinician_id !== clinicianId));
      toast.success('Disconnected from clinician');
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to disconnect'));
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleViewNewClinician = () => {
    if (redeemSuccess?.booking_slug) {
      navigate(`/patient/book/${redeemSuccess.booking_slug}`);
    }
    setRedeemSuccess(null);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-clinic-dark">My Clinicians</h1>
        <p className="text-clinic-muted mt-1">
          Connect with clinicians using invite codes and manage your connections.
        </p>
      </div>

      {/* Redeem Code Section */}
      <div className="mb-8">
        <RedeemCodeForm onSuccess={handleRedeemSuccess} />
      </div>

      {/* Connected Clinicians Section */}
      <div>
        <div className="flex items-center mb-4">
          <UserGroupIcon className="h-6 w-6 mr-2" style={{ color: colors.primary }} />
          <h2 className="text-lg font-semibold text-clinic-dark">Connected Clinicians</h2>
          <span className="ml-2 px-2 py-0.5 text-sm rounded-full bg-clinic-surface-sunken text-clinic-muted">
            {clinicians.length}
          </span>
        </div>

        {/* Error state */}
        {error && (
          <div className="mb-4 bg-clinic-accent/10 border border-clinic-accent/30 rounded-md p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-clinic-accent mr-2" />
              <p className="text-clinic-accent">{error}</p>
            </div>
            <button
              onClick={fetchClinicians}
              className="mt-2 text-sm text-clinic-accent hover:text-clinic-accent underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: colors.primary }}
            ></div>
          </div>
        ) : clinicians.length === 0 ? (
          /* Empty state */
          <div className="text-center py-12 bg-white rounded-lg border border-clinic-primary-lighter">
            <UserGroupIcon className="mx-auto h-12 w-12 text-clinic-muted" />
            <h3 className="mt-2 text-sm font-medium text-clinic-dark">No connected clinicians</h3>
            <p className="mt-1 text-sm text-clinic-muted">
              Enter an invite code above to connect with a clinician.
            </p>
            <p className="mt-3 text-sm text-clinic-muted">
              Or{' '}
              <a
                href="/clinicians"
                className="font-medium hover:underline"
                style={{ color: colors.primary }}
              >
                browse the public directory
              </a>{' '}
              to find available clinicians.
            </p>
          </div>
        ) : (
          /* Clinician list */
          <div className="space-y-3">
            {clinicians.map((clinician) => (
              <ClinicianCard
                key={clinician.clinician_id}
                clinician={clinician}
                onDisconnect={handleDisconnect}
                isDisconnecting={disconnectingId === clinician.clinician_id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Success Modal */}
      <RedeemSuccessModal
        isOpen={!!redeemSuccess}
        response={redeemSuccess}
        onClose={() => setRedeemSuccess(null)}
        onViewClinician={handleViewNewClinician}
      />
    </div>
  );
};

export default MyClinicians;
